import { FC, useEffect, useState } from 'react';
import { Details } from './Details';
import { DEPayment } from './DEPayment';
import { Review } from './Review';
import { Wizard } from './Wizard';
import { Row } from 'components/Row';
import { Body, BodySmall } from 'components/Typography';
import theme from 'config/theme';
import { NavigationAmountDetails, NavigationContainer, OlNavigation, ProgressBar } from '../styled';
import {
  formatNumberAsCurrency,
  getPathWithRef,
  getUrlParams,
  isKeyInObject,
  noop,
} from 'helpers/utils';
import { CandidateType } from 'components/Slate/Candidates/Candidates';
import { IUserData } from 'store/user/types';
import {
  getActionNoun,
  getDemocracyEngineCheckoutUrl,
  getIsTippingVariant2,
  getUrlSearchParamsStrWithoutRecipients,
  saveDonationSelections,
} from 'helpers/donate';
import { PaymentsData } from 'store/payments/types';
import {
  InitialValuesInterface,
  getFormErrors,
  getInitialActivePageIndex,
  getInitialValuesFromApiData,
  getInitialValuesFromLastPayment,
  getIsCheckoutMonthly,
  getIsEmployerAddressRequiredByCandidates,
  getPayRequestBody,
  getProgressBarWidth,
} from './utils';
import { Link, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'helpers/hooks';
import { postPayWithToken } from 'store/pay/actions';
import { postPayment, postPaymentAPI, postPaymentLoggedOut } from 'store/payments/actions';
import { getNewPaymentsById, getPaymentsState } from 'selectors/payments';
import { getDETokensById } from 'selectors/democracyEngine';
import { DETokenResponseData } from 'store/democracyengine/types';
import { getDonatePageData } from 'selectors/donatePage';
import { getPayData } from 'selectors/pay';
import { handleStop2EndDonationIfNeeded, getIsInStop2End } from 'helpers/stop2end';
import { FORM_ERROR } from 'final-form';
import { useGetFiltersQuery } from 'services/filtersApi';
import { EventSelections, getTicketCount, getTicketsWithSelections } from 'helpers/event';
import { getEventData } from 'selectors/eventPage';
import { ProcessingRedirectOptions } from 'store/donatePage/constants';
import { datadogLogs } from '@datadog/browser-logs';
import { clearDonationSelections } from 'services/storage';
import {
  trackDECheckoutDonationFailed,
  trackDECheckoutDonationSucceeded,
  trackCheckoutReviewViewed,
  trackCheckoutStep1Viewed,
  trackCompleteDonationClicked,
} from 'helpers/mixpanelEvents';
import type { MixpanelEventMap } from 'helpers/mixpanelEvents';
import { useStatsigClient } from '@statsig/react-bindings';
import { useDefaultTipAmount } from 'hooks/statsig';

const formPages = [
  {
    navigationTitle: '2. Personal Details',
  },
  {
    navigationTitle: '3. Payment Method',
  },
  {
    navigationTitle: '4. Review',
  },
];

interface Props {
  candidates: CandidateType[];
  clientId?: number;
  donationAmount: number;
  eventSelections?: EventSelections;
  pageOwner: string;
  payments: PaymentsData[];
  user?: IUserData;
}

export const CheckoutForm: FC<Props> = ({
  candidates,
  clientId,
  donationAmount,
  eventSelections,
  pageOwner,
  payments = [],
  user,
}) => {
  const { urlDonateAgain, urlEvent } = getUrlParams();
  const [activePageIndex, setActivePageIndex] = useState(0);
  const [initialValues, setInitalValues] = useState({});
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [democracyEngineCheckoutUrl, setDemocracyEngineCheckoutUrl] = useState('');
  const { newPaymentsById, deTokens, saveNewPaymentIsLoading, payment, page } = useAppSelector(
    state => ({
      newPaymentsById: getNewPaymentsById(state),
      saveNewPaymentIsLoading: getPaymentsState(state).saveNewPaymentIsLoading,
      deTokens: getDETokensById(state),
      payment: getPayData(state),
      page: urlEvent ? getEventData(state) : getDonatePageData(state),
    })
  );
  const { data: filtersResponse } = useGetFiltersQuery({ client_id: clientId });
  const filters = filtersResponse?.filters || [];
  const [tokensToSave, setTokensToSave] = useState([]);
  const { logEvent: statsigLogEvent } = useStatsigClient();
  const defaultTipAmount = useDefaultTipAmount(
    Number(donationAmount),
    getIsTippingVariant2(clientId)
  );

  useEffect(() => {
    if (candidates && donationAmount) {
      setDemocracyEngineCheckoutUrl(getDemocracyEngineCheckoutUrl(candidates, donationAmount));
    }
  }, [candidates, donationAmount]);

  useEffect(() => {
    if (!donationAmount) {
      return;
    }
    if (!urlDonateAgain || !payment) {
      setInitalValues(getInitialValuesFromApiData(user, payments, defaultTipAmount));
    } else if (payment) {
      setInitalValues(
        getInitialValuesFromLastPayment(payment, payments, defaultTipAmount, user, newPaymentsById)
      );
    }
  }, [user, payments, payment, donationAmount, urlDonateAgain, clientId, defaultTipAmount]);

  useEffect(() => {
    if (Object.keys(initialValues).length) {
      setActivePageIndex(
        getInitialActivePageIndex(initialValues as InitialValuesInterface, candidates)
      );
    }
  }, [initialValues]);

  const getPaymentTokenId = values => {
    const paymentToken = values.default_token;
    const savedPayment =
      payments?.find(payment => payment.payment_authorization_token === paymentToken) ||
      (isKeyInObject(paymentToken, newPaymentsById) && newPaymentsById[paymentToken]) ||
      null;
    return savedPayment?.id || null;
  };

  const onSubmit = async values => {
    if (page?.processing_redirect === ProcessingRedirectOptions.STRIPE) {
      clearDonationSelections();
      datadogLogs.logger.error(
        'User attempting to complete donation via DE on Stripe page. Redirecting to donate page.',
        {
          page_query_string: page.query_str,
          referrer: document.referrer,
        }
      );
      // Hard redirect to donate page to avoid potential issues with checking out with DE on a Stripe page
      window.location.href = getPathWithRef(`/donate?${getUrlSearchParamsStrWithoutRecipients()}`);
      return;
    }
    const isCheckoutMonthly = getIsCheckoutMonthly();
    if (isCheckoutMonthly && !user?.id) {
      // do not allow logged out user to make monthly donation
      return new Promise(resolve => {
        resolve({
          [FORM_ERROR]: 'Please log in if you would like to make a monthly recurring donation',
        });
      });
    }
    let payTokenId = getPaymentTokenId(values);
    if (isCheckoutMonthly && !payTokenId) {
      // if payment was somehow not saved in monthly checkout,
      // save it before submitting donation
      try {
        const last4 =
          values.payment_type === 'credit_card'
            ? values.cc_number.slice(-4)
            : values.ach_account_number?.slice(-4);
        const payment = await postPaymentAPI({
          first_name: values.first_name,
          last_name: values.last_name,
          email: values.email,
          payment_authorization_display_name: `${
            values.payment_type === 'credit_card' ? 'Credit Card' : 'Electronic Check'
          }: ...${last4}`,
          payment_authorization_expiration_date:
            values.payment_type === 'credit_card'
              ? `20${values.cc_exp.slice(-2)}-${values.cc_exp.slice(0, 2)}-01`
              : '9999-12-31',
          payment_authorization_method:
            values.payment_type === 'credit_card' ? 'CreditCard' : 'ElectronicCheck',
          payment_authorization_token: values.default_token,
          last_4: last4 || '',
        });
        payTokenId = payment.data.id;
      } catch {
        // do not allow user to make donation without saving payment
        return new Promise(resolve => {
          resolve({
            [FORM_ERROR]:
              'We were unable to save your payment method. Please check payment and try again to make a monthly recurring donation.',
          });
        });
      }
    }
    const requestBody = getPayRequestBody(
      values,
      candidates,
      page,
      filters,
      getIsCheckoutMonthly(),
      payTokenId,
      eventSelections?.event_tickets
        ? getTicketsWithSelections(eventSelections?.event_tickets)
        : undefined
    );
    trackCompleteDonationClicked({
      page_owner: pageOwner,
      is_recurring: isCheckoutMonthly,
      amount: donationAmount,
      is_stripe: false,
      has_tip_option: true,
      tip_amount: values.tip ?? 0,
      payment_processor: 'DE',
      client_id: clientId,
      is_express: false,
    });
    if (values.tip) {
      statsigLogEvent('tip_submitted', values.tip);
    }
    if (user && getIsInStop2End(requestBody.donor_email, user.id)) {
      handleStop2EndDonationIfNeeded(requestBody.ref_code, requestBody.donor_email, user?.id);
    }
    return new Promise(resolve => {
      dispatch(
        postPayWithToken(
          requestBody,
          payTokenId && !!user?.id ? 'stored_token' : 'one_time_token',
          !!user?.id
        )
      )
        .then(response => {
          resolve(response);
          statsigLogEvent('checkout_completed', donationAmount);
          navigate(getPathWithRef(`confirmation?${getUrlSearchParamsStrWithoutRecipients()}`));
          trackDECheckoutDonationSucceeded({
            paymentProcessor: 'DE',
            userId: user?.id || '',
            candidates,
            donationAmount,
          });
        })
        .catch(error => {
          try {
            datadogLogs.logger.warn(
              `Error creating DE payment on page ${page?.query_str}: ${error.message}.`
            );
          } catch {
            noop();
          }
          resolve(getFormErrors(error));
          trackDECheckoutDonationFailed({
            paymentProcessor: 'DE',
            userId: user?.id || '',
            candidates,
            donationAmount,
          });
        });
    });
  };

  const onBackToDonatePage = () => {
    saveDonationSelections(candidates, donationAmount);
  };

  const getCanSavePaymentToken = (tokenToSave: string) =>
    !saveNewPaymentIsLoading &&
    !isKeyInObject(tokenToSave, newPaymentsById) &&
    !!tokenToSave &&
    !!user;

  const onNavigateToReviewPage = () => {
    tokensToSave.forEach(tokenToSave => {
      if (!getCanSavePaymentToken(tokenToSave)) {
        return;
      }
      const savePayment = user ? postPayment : postPaymentLoggedOut;
      const savedToken = isKeyInObject(tokenToSave, deTokens)
        ? deTokens[tokenToSave]
        : ({} as DETokenResponseData);
      const last4 =
        savedToken.payment_method === 'credit_card'
          ? savedToken.cc_last_four
          : savedToken.ach_account_number?.slice(-4);
      dispatch(
        savePayment({
          first_name: savedToken.donor_first_name,
          last_name: savedToken.donor_last_name,
          email: savedToken.donor_email,
          payment_authorization_display_name: `${
            savedToken.payment_method === 'credit_card' ? 'Credit Card' : 'Electronic Check'
          }: ...${last4}`,
          payment_authorization_expiration_date:
            savedToken.payment_method === 'credit_card'
              ? `20${savedToken.cc_expiration.slice(-2)}-${savedToken.cc_expiration.slice(0, 2)}-01`
              : '9999-12-31',
          payment_authorization_method:
            savedToken.payment_method === 'credit_card' ? 'CreditCard' : 'ElectronicCheck',
          payment_authorization_token: tokenToSave,
          last_4: last4 || '',
        })
      );
    });
  };

  // Shared by both DE step-view events so their payloads can't drift apart. Typed against the
  // catalog, so a change here errors at this function rather than at the two JSX call sites.
  // Must stay a function: these read at fire time, not render time.
  const getCheckoutStepViewProps = (): MixpanelEventMap['Checkout Step 1 Viewed'] => ({
    is_recurring: getIsCheckoutMonthly(),
    client_id: clientId,
    amount: donationAmount,
    is_stripe: false,
    payment_processor: 'DE',
    is_express: false,
  });

  const backToDonatePath = getIsCheckoutMonthly()
    ? getPathWithRef(`/monthly?${getUrlSearchParamsStrWithoutRecipients()}`)
    : getPathWithRef(`/donate?${getUrlSearchParamsStrWithoutRecipients()}`);

  return (
    <>
      <Row>
        <NavigationContainer $justifyContent="space-between">
          <OlNavigation>
            <li>
              <Link to={backToDonatePath} onClick={onBackToDonatePage}>
                <BodySmall $fontType="semiBold" $color={theme.colors.inkBlue}>
                  1. Amount
                </BodySmall>
                <NavigationAmountDetails $fontType="semiBold" $color={theme.colors.inkBlue}>
                  {' ('}
                  {formatNumberAsCurrency(donationAmount)}
                  {urlEvent &&
                    eventSelections?.event_tickets &&
                    ` - ${getTicketCount(eventSelections.event_tickets)} ticket${
                      getTicketCount(eventSelections.event_tickets) === 1 ? '' : 's'
                    }`}
                  {')'}
                </NavigationAmountDetails>
              </Link>
            </li>
            {formPages.map(({ navigationTitle }, index) => (
              <li key={index}>
                <button
                  onClick={() => index < activePageIndex && setActivePageIndex(index)}
                  disabled={index >= activePageIndex}
                >
                  <BodySmall
                    $fontType={index <= activePageIndex ? 'semiBold' : 'regular'}
                    $color={
                      index <= activePageIndex ? theme.colors.inkBlue : theme.shadows.black(0.7)
                    }
                  >
                    {navigationTitle}
                  </BodySmall>
                </button>
              </li>
            ))}
          </OlNavigation>
          <ProgressBar $width={getProgressBarWidth(activePageIndex)} />
        </NavigationContainer>
      </Row>
      <div>
        <Body $color={theme.colors.black}>
          {getIsCheckoutMonthly() ? 'Start your monthly ' : 'Complete your '}
        </Body>
        {activePageIndex === 2 ? (
          <Body $color={theme.colors.black}>contribution:</Body>
        ) : (
          <Body $fontType="semiBold" $color={theme.colors.black}>
            {formatNumberAsCurrency(donationAmount)} contribution:
          </Body>
        )}
      </div>
      {urlEvent &&
      eventSelections &&
      getTicketsWithSelections(eventSelections.event_tickets).length ? (
        <BodySmall $color={theme.colors.gray}>
          {getTicketsWithSelections(eventSelections.event_tickets)
            .map(
              ticket =>
                `${ticket.quantity_selected} ${ticket.name} ticket${
                  ticket.quantity_selected == 1 ? '' : 's'
                }`
            )
            .join(', ')}
          {getTicketsWithSelections(eventSelections.event_tickets).length > 1 &&
          eventSelections?.custom_amount
            ? ','
            : null}
          {eventSelections.custom_amount ? ' & additional donation' : null}
        </BodySmall>
      ) : null}
      <Wizard
        actionNoun={getActionNoun(page)}
        initialValues={initialValues}
        backToDonatePath={backToDonatePath}
        onBackToDonatePage={onBackToDonatePage}
        onSubmit={onSubmit}
        activePageIndex={activePageIndex}
        setActivePageIndex={setActivePageIndex}
        isSkipToReviewEnabled={activePageIndex === 0 && payments?.length > 0}
        onNavigateToLastPage={onNavigateToReviewPage}
        validateArray={[
          () => ({}),
          values => {
            const errors = { default_token: undefined as undefined | string };
            if (
              !values.default_token ||
              values.default_token === 'cc_token' ||
              values.default_token === 'ach_token'
            ) {
              errors.default_token = 'Valid payment required';
            }
            return errors;
          },
          () => ({}),
        ]}
      >
        <Details
          candidates={candidates}
          isEmployerAddressRequired={getIsEmployerAddressRequiredByCandidates(candidates)}
          onDetailsView={() => trackCheckoutStep1Viewed(getCheckoutStepViewProps())}
        />
        <DEPayment
          candidates={candidates}
          democracyEngineCheckoutUrl={democracyEngineCheckoutUrl}
          donationAmount={donationAmount}
          setTokensToSave={setTokensToSave}
        />
        <Review
          donationAmount={donationAmount}
          isTippingVariant2={getIsTippingVariant2(clientId)}
          numCandidates={candidates.length}
          backToDonatePath={backToDonatePath}
          onBackToDonatePage={onBackToDonatePage}
          setActivePageIndex={setActivePageIndex}
          onView={() => trackCheckoutReviewViewed(getCheckoutStepViewProps())}
        />
      </Wizard>
    </>
  );
};
